<template>
<section
  class="offers-full-width"
  :class="{
    'offers-full-width--regular-variant': !ignoreAspectRatio,
    'offers-full-width--second-variant': ignoreAspectRatio,
    [`offers-full-width--${settings.full_width_variant_card_size === 'padded' ? 'padded' : 'full-screen'}`]: true,
    'container-padded': settings.full_width_variant_card_size !== 'full_screen',
  }"
  :style="{
    '--image-aspect-ratio': settings.image_aspect_ratio_width / settings.image_aspect_ratio_height,
    '--image-brightness': settings.image_brightness_percent,
  }"
>
  <!-- Using `.static-container` like this because we want the whole block to be colored white. Home page blocks have
  alternating white and primary colors. -->
  <div class="swiper-container" v-swiper="swiperParams">
    <div class="swiper-wrapper">
      <full-width-offer-card v-for="offer in offers"
                             :key="`full-width-offer-card-${offer.id}`"
                             :offer="offer"
      />
    </div>

    <div class="swiper-pagination" ref="swiperPagination"/>

    <div class="swiper-button-prev" ref="swiperButtonPrev">
      <i class="fa fa-chevron-left"/>
    </div>
    <div class="swiper-button-next" ref="swiperButtonNext">
      <i class="fa fa-chevron-right"/>
    </div>
  </div>
</section>
</template>

<script>
import { mapGetters } from 'vuex'
import ResponsiveImage from '@/components/images/ResponsiveImage'
import FullWidthOfferCard from '@/components/offers/FullWidthOfferCard'

export default {
  components: {
    ResponsiveImage,
    FullWidthOfferCard,
  },

  directives: {
    swiper: {
      async bind(el, binding, vnode) {
        // el.classList.add('swiper-not-ready');

        const styleP = import('swiper/css/swiper.min.css');
        const { Swiper, modules } = (await import('./fullWidthTreeshakenSwiper'));
        Swiper.use(modules);
        await styleP;

        if (el.swiperUnbound) return;

        // el.classList.remove('swiper-not-ready');
        // await vnode.context.$nextTick(); // So Swiper elements (e.g. pagination & navigation) are inserted.
        el.swiper = new Swiper(el, binding.value);
      },
      unbind(el) {
        el.swiper?.destroy();
        el.swiperUnbound = true;
      },
    },
  },
  computed: {
    ...mapGetters({
      settings: 'settings/homeOffers',
      offers: 'offers/offers',
    }),

    swiperParams() {
      return {
        loop: true,
        spaceBetween: 32,
        autoplay: {
          delay: 5000,
        },
        navigation: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        },
        pagination: {
          el: '.swiper-pagination',
        },
      };
    },
    // I.e. crop the image and display text on a white card.
    ignoreAspectRatio() {
      return this.settings.subvariant === 'aspectRatioIgnore' && !this.$store.getters['ui/isBreakpointActive']('sm');
    },
  },

  methods: {
    getImageAltText(offer) {
      if (this.settings.display_text) {
        return '';
      }
      return `${offer.name}\n${offer.body}`
    },
  },
}
</script>

<style lang="less">
@import '~@/assets/less/components/utilities';

@r: offers-full-width;

.@{r} {
  --card-padded: 1; // theme('padding.4')
  --image-rounded: 1;
  &--full-screen {
    --card-padded: 0;
    --image-rounded: 0;
  }

  .swiper-container {
    margin: calc(var(--card-padded) * theme('margin.4')) 0;
  }

  &__offer {
    @apply relative;
  }
  &__image-wrapper {
    img {
      object-fit: cover;
    }
  }
  &__text-wrapper-wrapper {
    @apply absolute inset-0;
  }
  &__text-wrapper {
    @apply absolute flex flex-col;
  }
  &__offer-title {
    @apply font-bold whitespace-pre-wrap;
  }
  &__offer-text {
    @apply text-sm whitespace-pre-wrap;
  }
  .swiper-button-prev,
  .swiper-button-next {
    @apply hidden;
  }

  .swiper-pagination {
    @apply hidden;
  }

  &--regular-variant {
    .@{r}__image-wrapper {
      .aspect-ratio-image(var(--image-aspect-ratio));
      img {
        filter: brightness(calc(var(--image-brightness) * 1%));
        border-radius: calc(var(--image-rounded) * theme('width.2'));
      }
    }
    .@{r}__text-wrapper {
      @apply inset-y-0 left-0 text-white justify-center;
      max-width: 26rem;
      margin: 0 4vw;
    }
    .@{r}__offer-title {
      @apply text-lg my-1 leading-none;
    }
  }
  &--second-variant {
    .@{r}__image-wrapper {
      height: 22.5rem;
      img {
        border-radius: calc(var(--image-rounded) * theme('width.4'));
        @apply w-full h-full;
      }
    }
    .@{r}__text-wrapper {
      @apply m-4 px-5 py-3 inset-x-0 bottom-0 bg-white rounded-lg;
    }
    .@{r}__offer-title {
      @apply text-2xl my-2 leading-none;
    }
    .@{r}__offer-text {
      @apply my-1;
    }
  }
}

@screen lg {
  .@{r} {
    &--regular-variant {
      @navigation-button-size: theme('width.10');
      @navigation-button-card-offset: theme('padding.4');

      .@{r}__text-wrapper {
        margin: 0 calc((@navigation-button-card-offset + @navigation-button-size) + theme('margin.2') + 5vw);
      }
      .@{r}__image-wrapper {
        img {
          border-radius: calc(var(--image-rounded) * theme('width.4'));
        }
      }
      .@{r}__offer-title {
        @apply text-2xl my-4;
      }
      .@{r}__offer-text {
        @apply text-base my-2;
      }

      --navigation-button-card-offset: @navigation-button-card-offset;
      @container-size: theme('screens.xl');
      @num-buttons: 2; // Duh
      @space-remaining-between-text-container-and-image-borders: calc((100% - @container-size) / @num-buttons);

      // This ugliness because https://github.com/less/less.js/issues/3463.
      // The second part of our `max` takes over only when the block is more than `@container-size` wide, currently this
      // is true only when `settings.full_width_variant_card_size === 'full_screen'`
      --max-second-part: calc(
        @space-remaining-between-text-container-and-image-borders / 2
        - @navigation-button-size / 2
      );
      @max: ~"max(
        var(--navigation-button-card-offset),
        var(--max-second-part)
      )";
      @navigation-button-swiper-container-offset: @max;
      .swiper-button-prev,
      .swiper-button-next {
        @apply flex bg-white rounded-full text-dark text-xl;
        width: @navigation-button-size;
        height: @navigation-button-size;
        &:after {
          @apply hidden;
        }
      }
      .swiper-button-prev {
        left: @navigation-button-swiper-container-offset;
      }
      .swiper-button-next {
        right: @navigation-button-swiper-container-offset;
      }

      .swiper-pagination {
        @apply block;
        font-size: 0; // Otherwise it doesn't tightly wrap the bullets.
        bottom: theme('margin.5') !important;
      }
      .swiper-pagination-bullet {
        @apply bg-grey opacity-100;
        @size: calc(theme('width.2') + 1px);
        width: @size !important;
        height: @size !important;
        @margin-x: calc(theme('width.2') - 1px);
        margin: 0 @margin-x !important;
        &-active {
          @apply bg-grey-darkest;
        }
      }
    }
  }
}

@screen xl {
  .@{r} {
    &--regular-variant {
      .@{r}__offer-title {
        @apply text-4xl;
      }
    }
  }
}
</style>
